import { useMemo, useState } from 'react';

import { useAppSelector } from 'hooks';

import {
  SimpleGrid,
  Box,
  Collapse,
  Button,
  Icon,
  useColorModeValue,
  Flex,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const Claims = ({ index }: { index: number }) => {
  const [isClaimsCollapsed, setIsClaimsCollapsed] = useState(true);

  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const claims = useAppSelector(
    (state) => state.autoPersonalForm.data.accidents_and_claims,
  );

  const otherLosses = useAppSelector(
    (state) => state.autoPersonalForm.data.other_losses || [],
  );

  const filteredClaims = useMemo(() => {
    const regularClaims = claims
      .filter((claim) => claim.driver_nr === index + 1)
      .map((claim) => ({ ...claim, type: 'claim' }));
    const otherLossItems = otherLosses
      .filter((loss) => loss.driver_nr === index + 1)
      .map((loss) => ({ ...loss, type: 'other_loss' }));
    return [...regularClaims, ...otherLossItems];
  }, [claims, otherLosses, index]);

  return (
    <Box p="20px" py="10px" borderTop="1px solid" borderColor={borderColor}>
      <Box>
        <Flex justifyContent="space-between" alignItems="center">
          <Box fontWeight="bold" fontSize="l">
            {`Claims (${filteredClaims.length})`}
          </Box>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsClaimsCollapsed(!isClaimsCollapsed)}
            rightIcon={
              <Icon as={isClaimsCollapsed ? ChevronDownIcon : ChevronUpIcon} />
            }
          >
            {isClaimsCollapsed ? 'Show Claims' : 'Hide Claims'}
          </Button>
        </Flex>
        <Collapse in={!isClaimsCollapsed} animateOpacity>
          {filteredClaims.length === 0 ? (
            <Box p={2} color="gray.500">
              No claims for driver reported on the quote
            </Box>
          ) : (
            <Box>
              <SimpleGrid
                columns={1}
                borderTop="1px solid"
                borderColor={borderColor}
                mt={2}
              >
                <Flex p={2} bg="gray.50" fontWeight="semibold">
                  <Box w="100px">Party</Box>
                  <Box w="150px" textAlign="center">
                    Vehicle Nr
                  </Box>
                  <Box w="150px">Date</Box>
                  <Box flex="1">Coverage Claim Paid Under</Box>
                  <Box w="120px">Paid</Box>
                  <Box flex="1">Details</Box>
                </Flex>
                {filteredClaims.map((claim, claimIndex) => (
                  <Flex
                    key={claimIndex}
                    p={2}
                    pb={claimIndex < filteredClaims.length - 1 ? 2 : 0}
                    borderBottom={
                      claimIndex < filteredClaims.length - 1 ? '1px solid' : ''
                    }
                    borderColor={borderColor}
                    alignItems="center"
                  >
                    <Box w="100px">
                      <Box
                        display="inline-block"
                        bg="gray.500"
                        color="white"
                        px={2}
                        py={0.5}
                        borderRadius="md"
                        fontSize="sm"
                      >
                        {claim.type === 'other_loss' ? '3rd' : '1st'}
                      </Box>
                    </Box>
                    <Box w="150px" textAlign="center">
                      {claim.auto_nr || '-'}
                    </Box>
                    <Box w="150px">{claim.date}</Box>
                    <Box flex="1">
                      {[
                        claim.is_bi && 'BI',
                        claim.is_pd && 'PD',
                        claim.is_ab && 'AB',
                        claim.is_dcpd && 'DCPD',
                        claim.is_ua && 'UA',
                        claim.is_coll_ap && 'COLL/AP',
                        claim.is_comp_sp && 'COMP/SP',
                      ]
                        .filter(Boolean)
                        .join(', ') || 'None'}
                    </Box>
                    <Box w="120px">${claim.paid || '0.00'}</Box>
                    <Box flex="1">{claim.details || '-'}</Box>
                  </Flex>
                ))}
              </SimpleGrid>
            </Box>
          )}
        </Collapse>
      </Box>
    </Box>
  );
};

export default Claims;
